import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Stack,
    Typography,
    CircularProgress,
    Alert,
    Divider,
    useTheme,
    useMediaQuery
} from '@mui/material';
import JobFormField from './JobFormField';
import { getFieldErrors, isFormValid } from '../utils/formValidator';
import jobParameterSchemas from '../config/jobParameterSchemas.json';

/**
 * DynamicJobForm Component
 * Main form component that renders all fields from the job parameter schema
 * Manages form state, validates in real-time, and handles submission
 */
const DynamicJobForm = ({
    jobType,
    onSubmit,
    isLoading = false,
    initialValues = {},
    onCancel
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Get schema for the selected job type - BEFORE hooks
    const jobSchema = jobParameterSchemas.jobTypes[jobType];
    const fields = jobSchema?.fields || {};

    // Initialize form state - ALL HOOKS MUST BE CALLED UNCONDITIONALLY
    const [formValues, setFormValues] = useState(() => {
        const initialState = {};
        Object.entries(fields).forEach(([fieldName, fieldSchema]) => {
            if (initialValues[fieldName] !== undefined) {
                initialState[fieldName] = initialValues[fieldName];
            } else if (fieldSchema.type === 'hidden') {
                initialState[fieldName] = fieldSchema.value;
            } else if (fieldSchema.defaultValue !== undefined) {
                // Use defaultValue from schema
                initialState[fieldName] = fieldSchema.defaultValue;
            } else if (fieldSchema.type === 'array') {
                initialState[fieldName] = [];
            } else if (fieldSchema.type === 'object') {
                // Initialize nested object with defaults
                const nestedDefaults = {};
                if (fieldSchema.fields) {
                    Object.entries(fieldSchema.fields).forEach(([nestedFieldName, nestedFieldSchema]) => {
                        nestedDefaults[nestedFieldName] = nestedFieldSchema.defaultValue !== undefined
                            ? nestedFieldSchema.defaultValue
                            : '';
                    });
                }
                initialState[fieldName] = nestedDefaults;
            } else {
                initialState[fieldName] = '';
            }
        });
        return initialState;
    });

    const [fieldErrors, setFieldErrors] = useState({});
    const [touched, setTouched] = useState({});

    // Reset form state when job type changes
    useEffect(() => {
        const initialState = {};
        Object.entries(fields).forEach(([fieldName, fieldSchema]) => {
            if (initialValues[fieldName] !== undefined) {
                initialState[fieldName] = initialValues[fieldName];
            } else if (fieldSchema.type === 'hidden') {
                initialState[fieldName] = fieldSchema.value;
            } else if (fieldSchema.defaultValue !== undefined) {
                // Use defaultValue from schema
                initialState[fieldName] = fieldSchema.defaultValue;
            } else if (fieldSchema.type === 'array') {
                initialState[fieldName] = [];
            } else if (fieldSchema.type === 'object') {
                // Initialize nested object with defaults
                const nestedDefaults = {};
                if (fieldSchema.fields) {
                    Object.entries(fieldSchema.fields).forEach(([nestedFieldName, nestedFieldSchema]) => {
                        nestedDefaults[nestedFieldName] = nestedFieldSchema.defaultValue !== undefined
                            ? nestedFieldSchema.defaultValue
                            : '';
                    });
                }
                initialState[fieldName] = nestedDefaults;
            } else {
                initialState[fieldName] = '';
            }
        });
        setFormValues(initialState);
        setFieldErrors({});
        setTouched({});

        console.log(`[DynamicJobForm] Job type changed to: ${jobType}`);
        console.log(`[DynamicJobForm] Form reset with initial values:`, initialState);
    }, [jobType]);

    // Validate form on value changes
    useEffect(() => {
        const errors = getFieldErrors(jobType, formValues);
        setFieldErrors(errors);

        // Console logging for debugging form validation
        console.log(`[DynamicJobForm] Form validation for job type: ${jobType}`);
        console.log(`[DynamicJobForm] Current form values:`, formValues);
        console.log(`[DynamicJobForm] Validation errors:`, errors);
        console.log(`[DynamicJobForm] Is form valid:`, Object.keys(errors).length === 0);
    }, [formValues, jobType]);

    // Early return for invalid job type - NOW AFTER ALL HOOKS
    if (!jobSchema) {
        return (
            <Alert severity="error">
                Invalid job type: {jobType}
            </Alert>
        );
    }

    // Handle field value change
    const handleFieldChange = (fieldName, value) => {
        setFormValues((prev) => ({
            ...prev,
            [fieldName]: value
        }));
        setTouched((prev) => ({
            ...prev,
            [fieldName]: true
        }));
    };

    // Handle field blur
    const handleFieldBlur = (fieldName) => {
        setTouched((prev) => ({
            ...prev,
            [fieldName]: true
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Mark all fields as touched
        const allTouched = {};
        Object.keys(fields).forEach((fieldName) => {
            allTouched[fieldName] = true;
        });
        setTouched(allTouched);

        // Validate form
        if (!isFormValid(jobType, formValues)) {
            return;
        }

        // Call onSubmit callback with validated data
        if (onSubmit) {
            await onSubmit(formValues);
        }
    };

    // Organize fields into configurable sections
    const nestedObjectFields = [];
    const fieldsBySection = {};
    const defaultSectionKey = 'advanced';
    const sectionOrder = ['basicInfo', 'advanced'];
    const sectionTitles = {
        basicInfo: 'Basic Information',
        advanced: 'Advanced Settings'
    };

    const formatSectionTitle = (sectionKey) =>
        sectionTitles[sectionKey] || sectionKey.replace(/[_-]/g, ' ');

    Object.entries(fields).forEach(([fieldName, fieldSchema]) => {
        if (fieldSchema.type === 'hidden') {
            return; // Skip hidden fields
        }

        if (fieldSchema.type === 'object') {
            nestedObjectFields.push([fieldName, fieldSchema]);
            return;
        }

        const sectionKey = fieldSchema.section || defaultSectionKey;
        if (!fieldsBySection[sectionKey]) {
            fieldsBySection[sectionKey] = [];
        }
        fieldsBySection[sectionKey].push([fieldName, fieldSchema]);
    });

    const isFormValid_ = isFormValid(jobType, formValues);

    // Console logging for submit button state
    console.log(`[DynamicJobForm] Submit button state - isFormValid: ${isFormValid_}, isLoading: ${isLoading}`);
    console.log(`[DynamicJobForm] Submit button will be ${isFormValid_ && !isLoading ? 'ENABLED' : 'DISABLED'}`);

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: '100%',
                // maxWidth: '800px',
                margin: '0 auto'
            }}
        >
            {/* Configurable Field Sections */}
            {[
                ...sectionOrder.filter((section) => fieldsBySection[section]?.length),
                ...Object.keys(fieldsBySection).filter((section) => !sectionOrder.includes(section))
            ].map((sectionKey) => (
                <Card
                    sx={{
                        marginBottom: '2rem',
                        backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5'
                    }}
                >
                    <CardContent>
                        <Typography
                            variant="h6"
                            sx={{
                                marginBottom: '1.5rem',
                                fontWeight: 600,
                                color: theme.palette.primary.main
                            }}
                        >
                            {formatSectionTitle(sectionKey)}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: isMobile
                                    ? '1fr'
                                    : 'repeat(4, minmax(0, 1fr))',
                                gap: '1rem'
                            }}
                        >
                            {fieldsBySection[sectionKey].map(([fieldName, fieldSchema]) => (
                                <Box key={fieldName}>
                                    <JobFormField
                                        fieldName={fieldName}
                                        fieldSchema={fieldSchema}
                                        value={formValues[fieldName]}
                                        onChange={(value) => handleFieldChange(fieldName, value)}
                                        onBlur={() => handleFieldBlur(fieldName)}
                                        error={touched[fieldName] ? fieldErrors[fieldName] : ''}
                                        fullWidth
                                    />
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            ))}

            {/* Nested Object Fields Section */}
            {nestedObjectFields.length > 0 && (
                <Card
                    sx={{
                        marginBottom: '2rem',
                        backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5'
                    }}
                >
                    <CardContent>
                        <Typography
                            variant="h6"
                            sx={{
                                marginBottom: '1.5rem',
                                fontWeight: 600,
                                color: theme.palette.primary.main
                            }}
                        >
                            Other Parameters
                        </Typography>
                        <Stack spacing={2}>
                            {nestedObjectFields.map(([fieldName, fieldSchema]) => (
                                <NestedObjectField
                                    key={fieldName}
                                    fieldName={fieldName}
                                    fieldSchema={fieldSchema}
                                    value={formValues[fieldName]}
                                    onChange={(value) => handleFieldChange(fieldName, value)}
                                    onBlur={() => handleFieldBlur(fieldName)}
                                    error={touched[fieldName] ? fieldErrors[fieldName] : ''}
                                    touched={touched[fieldName]}
                                />
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Form Actions */}
            <Divider sx={{ marginBottom: '2rem' }} />
            <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                sx={{
                    justifyContent: 'flex-end',
                    marginBottom: '2rem'
                }}
            >

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isFormValid_ || isLoading}
                    fullWidth={isMobile}
                    sx={{
                        minWidth: '150px'
                    }}
                >
                    {isLoading ? (
                        <>
                            <CircularProgress size={20} sx={{ marginRight: '0.5rem' }} />
                            Submitting...
                        </>
                    ) : (
                        'Submit Job'
                    )}
                </Button>
            </Stack>
        </Box>
    );
};

/**
 * NestedObjectField Component
 * Renders nested object fields (like fit_args) with their sub-fields
 */
const NestedObjectField = ({
    fieldName,
    fieldSchema,
    value,
    onChange,
    onBlur,
    error,
    touched
}) => {
    const theme = useTheme();
    const nestedFields = fieldSchema.fields || {};

    const handleNestedChange = (nestedFieldName, nestedValue) => {
        onChange({
            ...value,
            [nestedFieldName]: nestedValue
        });
    };

    return (
        <Box
            sx={{
                padding: '1rem',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#fafafa'
            }}
        >
            <Typography
                variant="subtitle2"
                sx={{
                    marginBottom: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                }}
            >
                {fieldSchema.label}
            </Typography>
            {fieldSchema.help && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        marginBottom: '1rem',
                        color: theme.palette.text.secondary
                    }}
                >
                    {fieldSchema.help}
                </Typography>
            )}
            <Stack spacing={2}>
                {Object.entries(nestedFields).map(([nestedFieldName, nestedFieldSchema]) => (
                    <Box key={nestedFieldName}>
                        <JobFormField
                            fieldName={nestedFieldName}
                            fieldSchema={nestedFieldSchema}
                            value={value?.[nestedFieldName] || ''}
                            onChange={(val) => handleNestedChange(nestedFieldName, val)}
                            onBlur={() => onBlur()}
                            error=""
                            fullWidth
                        />
                    </Box>
                ))}
            </Stack>
            {error && touched && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        marginTop: '0.5rem',
                        color: theme.palette.error.main
                    }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default DynamicJobForm;
